import { signOut, useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef } from 'react';

// Global flag to prevent multiple timeout notifications across all instances
let globalTimeoutHandled = false;
let globalTimeoutInProgress = false;

export function useSessionTimeout() {
  const { data: session } = useSession();
  const router = useRouter();
  const lastActivityRef = useRef(Date.now());
  const intervalRef = useRef(null);

  const handleActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    globalTimeoutHandled = false; // Reset timeout flag on activity
  }, []);

  const handleTimeout = useCallback(async () => {
    // Prevent multiple timeout calls globally
    if (globalTimeoutHandled || globalTimeoutInProgress) return;

    globalTimeoutInProgress = true;
    globalTimeoutHandled = true;

    // Clear the interval immediately to prevent further checks
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    try {
      // Sign out first
      await signOut({
        redirect: false, // We'll handle redirect manually
      });

      // Navigate to signin with session expired message
      // The signin page will handle displaying the message
      if (typeof window !== 'undefined') {
        router.push('/auth/signin?message=session_expired');
      }
    } catch (error) {
      console.error('Error during session timeout:', error);
      // Fallback to simple redirect
      if (typeof window !== 'undefined') {
        window.location.href = '/auth/signin?message=session_expired';
      }
    } finally {
      globalTimeoutInProgress = false;
    }
  }, [router]);

  useEffect(() => {
    if (!session || typeof window === 'undefined') return;

    // Clear any existing interval first
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((event) => window.addEventListener(event, handleActivity, { passive: true }));

    intervalRef.current = setInterval(() => {
      const MAX_INACTIVITY = 2 * 60 * 60 * 1000; // 2 hours

      if (!globalTimeoutHandled && Date.now() - lastActivityRef.current > MAX_INACTIVITY) {
        handleTimeout();
      }
    }, 60000); // Check every minute

    // Initialize activity timestamp
    handleActivity();

    return () => {
      events.forEach((event) => window.removeEventListener(event, handleActivity));
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [session, handleActivity, handleTimeout]);

  return null;
}
